import { AutoAssignmentStyle, User } from "../../client";
import { TooltipWrapper } from "../../components/tooltip/TooltipWrapper";
import styles from "./CompanyInfoHeader.module.css";
import { SafeReportResponse } from "./Conversion";
import { useUsers } from "./data";
import { formatAbbreviatedMoney } from "./Money";

type CompanyInfoHeaderProps = {
  specialtyPropertyInfo: SafeReportResponse;
};

const assignmentText = (users: User[], style: AutoAssignmentStyle): string => {
  const assignee_name =
    users.find((u) => u.id === style.original_assignee)?.full_name ??
    style.original_assignee;

  switch (style.assignment_type) {
    case "override_in_email":
      return `Auto-assigned to ${assignee_name} due to instructions in email`;
    case "round_robin":
      return `Auto-assigned to ${assignee_name} due to round-robin assignment schedule`;
  }
};

export const CompanyInfoHeader: React.FC<CompanyInfoHeaderProps> = ({
  specialtyPropertyInfo,
}) => {
  const industry =
    specialtyPropertyInfo.report_json.company_info.business_description;
  const users = useUsers()?.data?.all_company_users ?? [];
  return (
    <div className={styles.companyInfoContainer}>
      {specialtyPropertyInfo.policy_number && (
        <TooltipWrapper
          tooltipText="Policy Number"
          tooltipProps={{ position: "top" }}
        >
          <div className={styles.companyInfoComponentContainer}>
            <i className="pi pi-book" />
            <span>{specialtyPropertyInfo.policy_number}</span>
          </div>
        </TooltipWrapper>
      )}
      {industry && (
        <TooltipWrapper
          tooltipText="Business Description"
          tooltipProps={{ position: "top" }}
        >
          <div className={styles.companyInfoComponentContainer}>
            <i className="pi pi-briefcase" />
            <span>{industry}</span>
          </div>
        </TooltipWrapper>
      )}
      <TooltipWrapper
        tooltipText="Business Code"
        tooltipProps={{ position: "top" }}
      >
        <div className={styles.companyInfoComponentContainer}>
          <i className="pi pi-building" />
          <span>{`${specialtyPropertyInfo.report_json.naics_info.code_type?.toUpperCase()} ${
            specialtyPropertyInfo.report_json.naics_info.code
          }`}</span>
        </div>
      </TooltipWrapper>
      {specialtyPropertyInfo.precomputed_metadata?.total_tiv != null && (
        <TooltipWrapper
          tooltipText="Total TIV"
          tooltipProps={{ position: "top" }}
        >
          <div className={styles.companyInfoComponentContainer}>
            <i className="pi pi-chart-bar" />
            <span>
              {formatAbbreviatedMoney(
                specialtyPropertyInfo.precomputed_metadata.total_tiv
              )}
            </span>
          </div>
        </TooltipWrapper>
      )}
      {specialtyPropertyInfo.report_json.company_info.effective_date && (
        <TooltipWrapper
          tooltipText="Effective Date"
          tooltipProps={{ position: "top" }}
        >
          <div className={styles.companyInfoComponentContainer}>
            <i className="pi pi-calendar" />
            <span>
              {specialtyPropertyInfo.report_json.company_info.effective_date}
            </span>
          </div>
        </TooltipWrapper>
      )}
      {specialtyPropertyInfo.report_json.company_info.target_premium && (
        <TooltipWrapper
          tooltipText="Target Premium"
          tooltipProps={{ position: "top" }}
        >
          <div className={styles.companyInfoComponentContainer}>
            <i className="pi pi-bullseye" />
            <span>
              {formatAbbreviatedMoney(
                Number(
                  specialtyPropertyInfo.report_json.company_info.target_premium
                )
              )}
            </span>
          </div>
        </TooltipWrapper>
      )}
      {specialtyPropertyInfo.report_json.company_info.quote_by_date && (
        <TooltipWrapper
          tooltipText="Quote By Date"
          tooltipProps={{ position: "top" }}
        >
          <div className={styles.companyInfoComponentContainer}>
            <i className="pi pi-calendar-clock" />
            <span>
              {specialtyPropertyInfo.report_json.company_info.quote_by_date}
            </span>
          </div>
        </TooltipWrapper>
      )}
      {specialtyPropertyInfo.auto_assignment_style && (
        <TooltipWrapper
          tooltipText={assignmentText(
            users,
            specialtyPropertyInfo.auto_assignment_style
          )}
          tooltipProps={{ position: "top" }}
        >
          <div className={styles.companyInfoComponentContainer}>
            <i className="pi pi-sparkles" />
            <span>Automatically Assigned</span>
          </div>
        </TooltipWrapper>
      )}
    </div>
  );
};
